import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import {
  getSavedPaymentMethods,
  type SavedPaymentMethod,
} from '../transport/api/protectedApi';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('useProductPaymentMethods');

interface UseProductPaymentMethodsOptions {
  autoFetch?: boolean;
}

interface UseProductPaymentMethodsResult {
  paymentMethods: SavedPaymentMethod[];
  hasSavedMethod: boolean;
  isFetched: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useProductPaymentMethods(
  options: UseProductPaymentMethodsOptions = {}
): UseProductPaymentMethodsResult {
  const { autoFetch = true } = options;
  const { user } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState<SavedPaymentMethod[]>(
    []
  );
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(autoFetch && !!user);
  const [error, setError] = useState<string | null>(null);

  const fetchPaymentMethods = useCallback(async () => {
    if (!user) {
      setPaymentMethods([]);
      setLoading(false);
      setIsFetched(true);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getSavedPaymentMethods();
      if (response.success && response.data?.paymentMethods) {
        setPaymentMethods(response.data.paymentMethods);
      } else {
        setPaymentMethods([]);
        setError('Failed to load saved payment methods.');
      }
    } catch (fetchError) {
      logger.error('Failed to load saved payment methods', fetchError);
      setPaymentMethods([]);
      setError('Failed to load saved payment methods.');
    } finally {
      setIsFetched(true);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (autoFetch) {
      void fetchPaymentMethods();
    }
  }, [autoFetch, fetchPaymentMethods]);

  return {
    paymentMethods,
    hasSavedMethod: paymentMethods.length > 0,
    isFetched,
    loading,
    error,
    refetch: fetchPaymentMethods,
  };
}
